import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Loader2, X, ShieldAlert, Lightbulb } from "lucide-react";
import { api, fmtDate, SEVERITY_STYLES } from "@/lib/api";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-sm text-navy shadow-none focus:border-gold focus:outline-none";
const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

const EMPTY = { category: "", description: "", root_cause: "", potential_impact: "", likelihood: 3, impact: 3, response_type: "threat", response_strategy: "Mitigate", action_plan: "", risk_owner: "", target_date: "", residual_risk: "", review_date: "", status: "Open", supporting_docs: [] };

const HEAT_COLORS = (score) =>
  score >= 20 ? "bg-red-600 text-white" : score >= 15 ? "bg-orange-500 text-white" : score >= 10 ? "bg-amber-400 text-navy" : score >= 5 ? "bg-emerald-400 text-navy" : "bg-emerald-200 text-navy";

export default function Risks() {
  const [risks, setRisks] = useState([]);
  const [meta, setMeta] = useState({ categories: [], threat_responses: [], opportunity_responses: [] });
  const [heat, setHeat] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const load = () => {
    api.get("/api/app/risks").then((r) => setRisks(r.data));
    api.get("/api/app/risks/heatmap").then((r) => setHeat(r.data));
    api.get("/api/app/risks/meta").then((r) => setMeta(r.data));
    api.get("/api/app/risks/suggestions").then((r) => setSuggestions(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    if (params.get("new")) setEditing({ ...EMPTY });
  }, [params]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...editing, likelihood: Number(editing.likelihood), impact: Number(editing.impact),
      residual_risk: editing.residual_risk ? Number(editing.residual_risk) : null };
    try {
      if (editing.risk_id) await api.put(`/api/app/risks/${editing.risk_id}`, payload);
      else await api.post("/api/app/risks", payload);
      setEditing(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  const addSuggestion = async (s) => {
    await api.post("/api/app/risks", { ...s, status: "Open" });
    load();
  };

  const responses = editing?.response_type === "opportunity" ? meta.opportunity_responses : meta.threat_responses;

  return (
    <div className="space-y-8" data-testid="risks-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Risk Management</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Risk Register</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="risk-add-button" className="btn-gold !py-2.5">
          <Plus className="h-4 w-4" /> Add a Risk
        </button>
      </div>

      {heat.length > 0 && (
        <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="risk-heatmap">
          <h2 className="font-serif text-2xl font-semibold text-navy">Risk heat map</h2>
          <p className="mt-1 text-xs text-ink/50">Open risks by likelihood (rows, 1–5) and impact (columns, 1–5).</p>
          <div className="mt-5 inline-block">
            <div className="grid grid-cols-6 gap-1.5">
              <span />
              {[1, 2, 3, 4, 5].map((i) => <span key={i} className="text-center text-[10px] font-semibold text-navy/50">IMPACT {i}</span>)}
              {heat.map((row) => (
                <div key={row[0].likelihood} className="contents" data-testid={`heatmap-row-${row[0].likelihood}`}>
                  <span className="flex items-center text-[10px] font-semibold text-navy/50">LIKELIHOOD {row[0].likelihood}</span>
                  {row.map((cell) => {
                    const score = cell.likelihood * cell.impact;
                    return (
                      <span key={cell.impact} data-testid={`heatmap-cell-${cell.likelihood}-${cell.impact}`}
                            className={`flex h-12 w-12 items-center justify-center rounded-sm text-sm font-bold ${HEAT_COLORS(score)} ${cell.count ? "ring-2 ring-navy ring-offset-1" : ""}`}>
                        {cell.count || ""}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {suggestions.some((s) => !s.already_added) && (
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-6" data-testid="risk-suggestions">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-navy">
            <Lightbulb className="h-5 w-5 text-golddark" /> Suggested for your industry
          </h2>
          <p className="mt-1 text-xs text-ink/50">Based on your onboarding. Review each one — nothing is added until you confirm.</p>
          <div className="mt-4 space-y-3">
            {suggestions.filter((s) => !s.already_added).map((s) => (
              <div key={s.description} className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-navy/10 bg-white p-4" data-testid="risk-suggestion-item">
                <div className="max-w-xl">
                  <p className="text-sm font-semibold text-navy">{s.description}</p>
                  <p className="mt-0.5 text-xs text-ink/50">{s.category} · L{s.likelihood} × I{s.impact} · {s.response_strategy}</p>
                </div>
                <button onClick={() => addSuggestion(s)} data-testid="risk-suggestion-add" className="rounded-full border border-gold px-4 py-2 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold">
                  Confirm & Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white" data-testid="risks-table">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["ID", "Description", "Category", "L×I", "Score", "Severity", "Owner", "Target", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {risks.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-ink/50">No risks yet. Add your first risk or confirm a suggestion above.</td></tr>
            )}
            {risks.map((risk) => (
              <tr key={risk.risk_id} className="border-b border-navy/5 hover:bg-paper/60" data-testid={`risk-row-${risk.risk_id}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{risk.risk_id}</td>
                <td className="max-w-xs px-4 py-3">
                  <p className="line-clamp-2 font-medium text-navy">{risk.description}</p>
                  <span className="text-[11px] text-ink/40">{risk.response_type === "opportunity" ? "Opportunity" : "Threat"} · {risk.response_strategy}</span>
                </td>
                <td className="px-4 py-3 text-ink/60">{risk.category}</td>
                <td className="px-4 py-3 text-ink/60">{risk.likelihood}×{risk.impact}</td>
                <td className="px-4 py-3 font-bold text-navy">{risk.score}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${SEVERITY_STYLES[risk.severity] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{risk.severity}</span>
                </td>
                <td className="px-4 py-3 text-ink/60">{risk.risk_owner || "—"}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(risk.target_date)}</td>
                <td className="px-4 py-3 text-ink/60">{risk.status}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setEditing({ ...risk })} data-testid={`risk-edit-${risk.risk_id}`} className="text-sm font-semibold text-golddark hover:text-navy">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/50" onClick={() => setEditing(null)} data-testid="risk-drawer-backdrop">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-white p-8" data-testid="risk-drawer">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-navy">{editing.risk_id ? `Edit ${editing.risk_id}` : "Add a Risk"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" data-testid="risk-drawer-close"><X className="h-5 w-5 text-navy/50" /></button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={label}>Risk description *</label>
                <textarea required minLength={3} className={`${field} min-h-16`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="risk-input-description" />
              </div>
              <div>
                <label className={label}>Category *</label>
                <select className={field} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="risk-input-category" required>
                  <option value="">Select…</option>
                  {meta.categories?.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Type</label>
                <select className={field} value={editing.response_type} onChange={(e) => setEditing({ ...editing, response_type: e.target.value, response_strategy: e.target.value === "opportunity" ? "Exploit" : "Mitigate" })} data-testid="risk-input-type">
                  <option value="threat">Threat</option>
                  <option value="opportunity">Opportunity</option>
                </select>
              </div>
              <div>
                <label className={label}>Root cause</label>
                <input className={field} value={editing.root_cause} onChange={(e) => setEditing({ ...editing, root_cause: e.target.value })} data-testid="risk-input-root-cause" />
              </div>
              <div>
                <label className={label}>Potential business impact</label>
                <input className={field} value={editing.potential_impact} onChange={(e) => setEditing({ ...editing, potential_impact: e.target.value })} data-testid="risk-input-impact" />
              </div>
              <div>
                <label className={label}>Likelihood (1–5) *</label>
                <input type="number" min="1" max="5" required className={field} value={editing.likelihood} onChange={(e) => setEditing({ ...editing, likelihood: e.target.value })} data-testid="risk-input-likelihood" />
              </div>
              <div>
                <label className={label}>Impact (1–5) *</label>
                <input type="number" min="1" max="5" required className={field} value={editing.impact} onChange={(e) => setEditing({ ...editing, impact: e.target.value })} data-testid="risk-input-impact-score" />
              </div>
              <div>
                <label className={label}>Response strategy *</label>
                <select className={field} value={editing.response_strategy} onChange={(e) => setEditing({ ...editing, response_strategy: e.target.value })} data-testid="risk-input-strategy" required>
                  {(responses || ["Mitigate"]).map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Status</label>
                <select className={field} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="risk-input-status">
                  {["Open", "In Progress", "Closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Risk owner</label>
                <input className={field} value={editing.risk_owner} onChange={(e) => setEditing({ ...editing, risk_owner: e.target.value })} data-testid="risk-input-owner" />
              </div>
              <div>
                <label className={label}>Target date</label>
                <input type="date" className={field} value={editing.target_date || ""} onChange={(e) => setEditing({ ...editing, target_date: e.target.value })} data-testid="risk-input-target-date" />
              </div>
              <div>
                <label className={label}>Residual risk (1–25)</label>
                <input type="number" min="1" max="25" className={field} value={editing.residual_risk ?? ""} onChange={(e) => setEditing({ ...editing, residual_risk: e.target.value })} data-testid="risk-input-residual" />
              </div>
              <div>
                <label className={label}>Review date</label>
                <input type="date" className={field} value={editing.review_date || ""} onChange={(e) => setEditing({ ...editing, review_date: e.target.value })} data-testid="risk-input-review-date" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Mitigation / action plan</label>
                <textarea className={`${field} min-h-16`} value={editing.action_plan} onChange={(e) => setEditing({ ...editing, action_plan: e.target.value })} data-testid="risk-input-action-plan" />
              </div>
            </div>

            <p className="mt-4 rounded-sm border border-gold/40 bg-gold/5 p-3 text-xs italic text-navy/70">
              Score = likelihood × impact. Severity labels use the thresholds your TSM administrators configure.
            </p>

            <div className="mt-6 flex gap-3">
              <button type="submit" disabled={busy} data-testid="risk-save-button" className="btn-gold flex-1 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
                Save Risk
              </button>
              {editing.risk_id && (
                <button type="button" onClick={async () => { await api.delete(`/api/app/risks/${editing.risk_id}`); setEditing(null); load(); }} data-testid="risk-delete-button" className="rounded-sm border border-red-200 px-5 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
